// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

import type { AzureSupportedClouds } from "./static-helpers/cloudSettingHelpers.js";
import { AzureClouds } from "./static-helpers/cloudSettingHelpers.js";
import type {
  PageSettings,
  ContinuablePage,
  PagedAsyncIterableIterator,
} from "./static-helpers/pagingHelpers.js";

export { ContainerServiceClient } from "./containerServiceClient.js";
export type { SimplePollerLike } from "./static-helpers/simplePollerHelpers.js";
export type { RestorePollerOptions } from "./restorePollerHelpers.js";
export { restorePoller } from "./restorePollerHelpers.js";
export type {
  AgentPool,
  ManagedClusterAgentPoolProfileProperties,
  OSDiskType,
  KubeletDiskType,
  WorkloadRuntime,
  PodIPAllocationMode,
  OSType,
  Ossku,
  ScaleDownMode,
  AgentPoolType,
  AgentPoolMode,
  AgentPoolUpgradeSettings,
  UndrainableNodeBehavior,
  PowerState,
  Code,
  ScaleSetPriority,
  ScaleSetEvictionPolicy,
  KubeletConfig,
  LinuxOSConfig,
  SysctlConfig,
  GPUInstanceProfile,
  CreationData,
  AgentPoolNetworkProfile,
  IPTag,
  PortRange,
  Protocol,
  AgentPoolWindowsProfile,
  AgentPoolSecurityProfile,
  AgentPoolSSHAccess,
  GPUProfile,
  GPUDriver,
  AgentPoolGatewayProfile,
  VirtualMachinesProfile,
  ScaleProfile,
  ManualScaleProfile,
  VirtualMachineNodes,
  AgentPoolStatus,
  ErrorDetail,
  ErrorAdditionalInfo,
  LocalDNSProfile,
  LocalDNSMode,
  LocalDNSState,
  LocalDNSOverride,
  LocalDNSQueryLogging,
  LocalDNSProtocol,
  LocalDNSForwardDestination,
  LocalDNSForwardPolicy,
  LocalDNSServeStale,
  ProxyResource,
  Resource,
  SystemData,
  CreatedByType,
  ErrorResponse,
  AgentPoolDeleteMachinesParameter,
  AgentPoolAvailableVersions,
  AgentPoolAvailableVersionsProperties,
  AgentPoolAvailableVersionsPropertiesAgentPoolVersionsItem,
  AgentPoolUpgradeProfile,
  AgentPoolUpgradeProfileProperties,
  AgentPoolUpgradeProfilePropertiesUpgradesItem,
  ManagedCluster,
  ManagedClusterProperties,
  ManagedClusterAgentPoolProfile,
  ContainerServiceLinuxProfile,
  ContainerServiceSshConfiguration,
  ContainerServiceSshPublicKey,
  ManagedClusterWindowsProfile,
  LicenseType,
  WindowsGmsaProfile,
  ManagedClusterServicePrincipalProfile,
  ManagedClusterAddonProfile,
  ManagedClusterAddonProfileIdentity,
  ManagedClusterPodIdentityProfile,
  ManagedClusterPodIdentity,
  UserAssignedIdentity,
  ManagedClusterPodIdentityProvisioningState,
  ManagedClusterPodIdentityProvisioningInfo,
  ManagedClusterPodIdentityProvisioningError,
  ManagedClusterPodIdentityProvisioningErrorBody,
  ManagedClusterPodIdentityException,
  ManagedClusterOidcIssuerProfile,
  ManagedClusterNodeResourceGroupProfile,
  RestrictionLevel,
  KubernetesSupportPlan,
  ContainerServiceNetworkProfile,
  NetworkPlugin,
  NetworkPluginMode,
  NetworkPolicy,
  NetworkMode,
  NetworkDataplane,
  AdvancedNetworking,
  AdvancedNetworkingObservability,
  AdvancedNetworkingSecurity,
  AdvancedNetworkPolicies,
  AdvancedNetworkingSecurityTransitEncryption,
  TransitEncryptionType,
  AdvancedNetworkingPerformance,
  AccelerationMode,
  OutboundType,
  LoadBalancerSku,
  ManagedClusterLoadBalancerProfile,
  ManagedClusterLoadBalancerProfileManagedOutboundIPs,
  ManagedClusterLoadBalancerProfileOutboundIPPrefixes,
  ResourceReference,
  ManagedClusterLoadBalancerProfileOutboundIPs,
  BackendPoolType,
  ManagedClusterNATGatewayProfile,
  ManagedClusterManagedOutboundIPProfile,
  ManagedClusterStaticEgressGatewayProfile,
  IpFamily,
  ManagedClusterAADProfile,
  ManagedClusterAutoUpgradeProfile,
  UpgradeChannel,
  NodeOSUpgradeChannel,
  ClusterUpgradeSettings,
  UpgradeOverrideSettings,
  ManagedClusterPropertiesAutoScalerProfile,
  Expander,
  ManagedClusterAPIServerAccessProfile,
  PrivateLinkResource,
  ManagedClusterHttpProxyConfig,
  ManagedClusterSecurityProfile,
  ManagedClusterSecurityProfileDefender,
  ManagedClusterSecurityProfileDefenderSecurityMonitoring,
  AzureKeyVaultKms,
  KeyVaultNetworkAccessTypes,
  ManagedClusterSecurityProfileWorkloadIdentity,
  ManagedClusterSecurityProfileImageCleaner,
  ManagedClusterStorageProfile,
  ManagedClusterStorageProfileDiskCSIDriver,
  ManagedClusterStorageProfileFileCSIDriver,
  ManagedClusterStorageProfileSnapshotController,
  ManagedClusterStorageProfileBlobCSIDriver,
  ManagedClusterIngressProfile,
  ManagedClusterIngressProfileWebAppRouting,
  ManagedClusterIngressProfileNginx,
  NginxIngressControllerType,
  PublicNetworkAccess,
  ManagedClusterWorkloadAutoScalerProfile,
  ManagedClusterWorkloadAutoScalerProfileKeda,
  ManagedClusterWorkloadAutoScalerProfileVerticalPodAutoscaler,
  ManagedClusterAzureMonitorProfile,
  ManagedClusterAzureMonitorProfileMetrics,
  ManagedClusterAzureMonitorProfileKubeStateMetrics,
  ServiceMeshProfile,
  ServiceMeshMode,
  IstioServiceMesh,
  IstioComponents,
  IstioIngressGateway,
  IstioIngressGatewayMode,
  IstioEgressGateway,
  ProxyRedirectionMechanism,
  IstioCertificateAuthority,
  IstioPluginCertificateAuthority,
  ManagedClusterMetricsProfile,
  ManagedClusterCostAnalysis,
  ManagedClusterNodeProvisioningProfile,
  NodeProvisioningMode,
  NodeProvisioningDefaultNodePools,
  ManagedClusterBootstrapProfile,
  ArtifactSource,
  ManagedClusterAIToolchainOperatorProfile,
  ManagedClusterStatus,
  ManagedClusterSKU,
  ManagedClusterSKUName,
  ManagedClusterSKUTier,
  ExtendedLocation,
  ExtendedLocationTypes,
  ManagedClusterIdentity,
  ResourceIdentityType,
  DelegatedResource,
  ManagedServiceIdentityUserAssignedIdentitiesValue,
  TrackedResource,
  TagsObject,
  ManagedClusterAccessProfile,
  AccessProfile,
  CredentialResults,
  CredentialResult,
  RunCommandRequest,
  RunCommandResult,
  CommandResultProperties,
  OutboundEnvironmentEndpoint,
  EndpointDependency,
  EndpointDetail,
  ManagedClusterUpgradeProfile,
  ManagedClusterUpgradeProfileProperties,
  ManagedClusterPoolUpgradeProfile,
  ManagedClusterPoolUpgradeProfileUpgradesItem,
  MeshRevisionProfile,
  MeshRevisionProfileProperties,
  MeshRevision,
  CompatibleVersions,
  MeshUpgradeProfile,
  MeshUpgradeProfileProperties,
  KubernetesVersionListResult,
  KubernetesVersion,
  KubernetesVersionCapabilities,
  KubernetesPatchVersion,
  MaintenanceConfiguration,
  MaintenanceConfigurationProperties,
  TimeInWeek,
  WeekDay,
  TimeSpan,
  MaintenanceWindow,
  Schedule,
  DailySchedule,
  WeeklySchedule,
  AbsoluteMonthlySchedule,
  RelativeMonthlySchedule,
  Type,
  DateSpan,
  ManagedNamespace,
  NamespaceProperties,
  NamespaceProvisioningState,
  ResourceQuota,
  NetworkPolicies,
  PolicyRule,
  AdoptionPolicy,
  DeletePolicy,
  Machine,
  MachineProperties,
  MachineNetworkProperties,
  MachineIpAddress,
  PrivateEndpointConnection,
  PrivateEndpointConnectionProperties,
  PrivateEndpointConnectionProvisioningState,
  PrivateEndpoint,
  PrivateLinkServiceConnectionState,
  ConnectionStatus,
  PrivateEndpointConnectionListResult,
  Snapshot,
  SnapshotProperties,
  SnapshotType,
  TrustedAccessRoleBinding,
  TrustedAccessRoleBindingProperties,
  TrustedAccessRoleBindingProvisioningState,
  OperationValue,
  OperationValueDisplay,
  PrivateLinkResourcesListResult,
  TrustedAccessRole,
  TrustedAccessRoleRule,
  Format,
} from "./models/index.js";
export {
  KnownOSDiskType,
  KnownKubeletDiskType,
  KnownWorkloadRuntime,
  KnownPodIPAllocationMode,
  KnownOSType,
  KnownOssku,
  KnownScaleDownMode,
  KnownAgentPoolType,
  KnownAgentPoolMode,
  KnownUndrainableNodeBehavior,
  KnownCode,
  KnownScaleSetPriority,
  KnownScaleSetEvictionPolicy,
  KnownGPUInstanceProfile,
  KnownProtocol,
  KnownAgentPoolSSHAccess,
  KnownGPUDriver,
  KnownLocalDNSMode,
  KnownLocalDNSState,
  KnownLocalDNSQueryLogging,
  KnownLocalDNSProtocol,
  KnownLocalDNSForwardDestination,
  KnownLocalDNSForwardPolicy,
  KnownLocalDNSServeStale,
  KnownCreatedByType,
  KnownLicenseType,
  KnownManagedClusterPodIdentityProvisioningState,
  KnownRestrictionLevel,
  KnownKubernetesSupportPlan,
  KnownNetworkPlugin,
  KnownNetworkPluginMode,
  KnownNetworkPolicy,
  KnownNetworkMode,
  KnownNetworkDataplane,
  KnownAdvancedNetworkPolicies,
  KnownTransitEncryptionType,
  KnownAccelerationMode,
  KnownOutboundType,
  KnownLoadBalancerSku,
  KnownBackendPoolType,
  KnownIpFamily,
  KnownUpgradeChannel,
  KnownNodeOSUpgradeChannel,
  KnownExpander,
  KnownKeyVaultNetworkAccessTypes,
  KnownNginxIngressControllerType,
  KnownPublicNetworkAccess,
  KnownServiceMeshMode,
  KnownIstioIngressGatewayMode,
  KnownProxyRedirectionMechanism,
  KnownNodeProvisioningMode,
  KnownNodeProvisioningDefaultNodePools,
  KnownArtifactSource,
  KnownManagedClusterSKUName,
  KnownManagedClusterSKUTier,
  KnownExtendedLocationTypes,
  KnownResourceIdentityType,
  KnownWeekDay,
  KnownType,
  KnownNamespaceProvisioningState,
  KnownPolicyRule,
  KnownAdoptionPolicy,
  KnownDeletePolicy,
  KnownPrivateEndpointConnectionProvisioningState,
  KnownConnectionStatus,
  KnownSnapshotType,
  KnownTrustedAccessRoleBindingProvisioningState,
  KnownFormat,
  KnownVersions,
} from "./models/index.js";
export type { ContainerServiceClientOptionalParams } from "./api/index.js";
export type {
  AgentPoolsGetUpgradeProfileOptionalParams,
  AgentPoolsGetAvailableAgentPoolVersionsOptionalParams,
  AgentPoolsUpgradeNodeImageVersionOptionalParams,
  AgentPoolsDeleteMachinesOptionalParams,
  AgentPoolsAbortLatestOperationOptionalParams,
  AgentPoolsListOptionalParams,
  AgentPoolsDeleteOptionalParams,
  AgentPoolsCreateOrUpdateOptionalParams,
  AgentPoolsGetOptionalParams,
} from "./api/agentPools/index.js";
export type {
  MachinesListOptionalParams,
  MachinesGetOptionalParams,
} from "./api/machines/index.js";
export type {
  MaintenanceConfigurationsListByManagedClusterOptionalParams,
  MaintenanceConfigurationsDeleteOptionalParams,
  MaintenanceConfigurationsCreateOrUpdateOptionalParams,
  MaintenanceConfigurationsGetOptionalParams,
} from "./api/maintenanceConfigurations/index.js";
export type {
  ManagedClustersListKubernetesVersionsOptionalParams,
  ManagedClustersListMeshUpgradeProfilesOptionalParams,
  ManagedClustersGetMeshUpgradeProfileOptionalParams,
  ManagedClustersListMeshRevisionProfilesOptionalParams,
  ManagedClustersGetMeshRevisionProfileOptionalParams,
  ManagedClustersGetUpgradeProfileOptionalParams,
  ManagedClustersListOutboundNetworkDependenciesEndpointsOptionalParams,
  ManagedClustersGetCommandResultOptionalParams,
  ManagedClustersRunCommandOptionalParams,
  ManagedClustersStartOptionalParams,
  ManagedClustersStopOptionalParams,
  ManagedClustersRotateServiceAccountSigningKeysOptionalParams,
  ManagedClustersAbortLatestOperationOptionalParams,
  ManagedClustersRotateClusterCertificatesOptionalParams,
  ManagedClustersResetAADProfileOptionalParams,
  ManagedClustersResetServicePrincipalProfileOptionalParams,
  ManagedClustersListClusterMonitoringUserCredentialsOptionalParams,
  ManagedClustersListClusterUserCredentialsOptionalParams,
  ManagedClustersListClusterAdminCredentialsOptionalParams,
  ManagedClustersGetAccessProfileOptionalParams,
  ManagedClustersListOptionalParams,
  ManagedClustersListByResourceGroupOptionalParams,
  ManagedClustersDeleteOptionalParams,
  ManagedClustersUpdateTagsOptionalParams,
  ManagedClustersCreateOrUpdateOptionalParams,
  ManagedClustersGetOptionalParams,
} from "./api/managedClusters/index.js";
export type {
  ManagedNamespacesListCredentialOptionalParams,
  ManagedNamespacesListByManagedClusterOptionalParams,
  ManagedNamespacesDeleteOptionalParams,
  ManagedNamespacesUpdateOptionalParams,
  ManagedNamespacesCreateOrUpdateOptionalParams,
  ManagedNamespacesGetOptionalParams,
} from "./api/managedNamespaces/index.js";
export type { OperationsListOptionalParams } from "./api/operations/index.js";
export type {
  PrivateEndpointConnectionsListOptionalParams,
  PrivateEndpointConnectionsDeleteOptionalParams,
  PrivateEndpointConnectionsUpdateOptionalParams,
  PrivateEndpointConnectionsGetOptionalParams,
} from "./api/privateEndpointConnections/index.js";
export type { PrivateLinkResourcesListOptionalParams } from "./api/privateLinkResources/index.js";
export type { ResolvePrivateLinkServiceIdPostOptionalParams } from "./api/resolvePrivateLinkServiceId/index.js";
export type {
  SnapshotsListOptionalParams,
  SnapshotsListByResourceGroupOptionalParams,
  SnapshotsDeleteOptionalParams,
  SnapshotsUpdateTagsOptionalParams,
  SnapshotsCreateOrUpdateOptionalParams,
  SnapshotsGetOptionalParams,
} from "./api/snapshots/index.js";
export type {
  TrustedAccessRoleBindingsListOptionalParams,
  TrustedAccessRoleBindingsDeleteOptionalParams,
  TrustedAccessRoleBindingsCreateOrUpdateOptionalParams,
  TrustedAccessRoleBindingsGetOptionalParams,
} from "./api/trustedAccessRoleBindings/index.js";
export type { TrustedAccessRolesListOptionalParams } from "./api/trustedAccessRoles/index.js";
export type {
  AgentPoolsOperations,
  MachinesOperations,
  MaintenanceConfigurationsOperations,
  ManagedClustersOperations,
  ManagedNamespacesOperations,
  OperationsOperations,
  PrivateEndpointConnectionsOperations,
  PrivateLinkResourcesOperations,
  ResolvePrivateLinkServiceIdOperations,
  SnapshotsOperations,
  TrustedAccessRoleBindingsOperations,
  TrustedAccessRolesOperations,
} from "./classic/index.js";
export type { PageSettings, ContinuablePage, PagedAsyncIterableIterator };
export { AzureClouds };
export type { AzureSupportedClouds };
